import { useMemo } from 'react'
import { SipPlanMode } from '../../../api/types'
import {
  isSipIdealPlansFetchFailed,
  isSipIdealPlansLoading,
  useSipPlansContext
} from '../../SipPlansProvider'
import type { DerivedIdealCategory } from '../../deriveSipPlansView'
import { SipPlansListEmptyState } from '../../sip-ui-components/SipPlansListEmptyState'
import { PlansCategoryListSkeleton } from '../../../../lumpsum/plans-list/lumpsum-ui-components/PlansCategoryListSkeleton'
import { IdealSipCategoryCard } from './IdealSipCategoryCard'
import { IdealSipPlansListErrorState } from './IdealSipPlansListErrorState'
import { isSipIdealPlansListFullyEmpty } from '../../../../utils/sip/planListUtils'

type IdealSipCategoryListProps = {
  /** When set, render these instead of context and skip query loading/error/empty shells. */
  categories?: DerivedIdealCategory[] | null
}

export function IdealSipCategoryList({
  categories: dummyCategories
}: IdealSipCategoryListProps = {}) {
  const { derivedPlans, plansOverview, plansQuery } = useSipPlansContext()
  const hasDummyCategories = dummyCategories !== undefined

  const idealCategories = useMemo(() => {
    const categories = hasDummyCategories
      ? (dummyCategories ?? [])
      : (derivedPlans?.idealCategories ?? [])
    if (!categories.length) {
      return []
    }
    return categories.filter((category) => (category?.idealSips ?? []).length > 0)
  }, [hasDummyCategories, dummyCategories, derivedPlans?.idealCategories])

  if (!hasDummyCategories) {
    if (isSipIdealPlansLoading(SipPlanMode.IDEAL_SIPS, plansOverview != null, plansQuery)) {
      return <PlansCategoryListSkeleton showBuckets={false} />
    }

    if (isSipIdealPlansFetchFailed(SipPlanMode.IDEAL_SIPS, plansQuery)) {
      return <IdealSipPlansListErrorState onRetry={plansQuery.refetch} />
    }

    if (isSipIdealPlansListFullyEmpty(derivedPlans?.idealCategories)) {
      return (
        <SipPlansListEmptyState
          planMode={SipPlanMode.IDEAL_SIPS}
          planSnapshotDate={plansOverview?.planSnapshotDate}
          planDurationInMonths={plansOverview?.planDurationInMonths}
        />
      )
    }
  }

  return (
    <>
      <IdealSipCategoryListStickyBar categoryCount={idealCategories.length} />
      {idealCategories.map((category, categoryIndex) => (
        <IdealSipCategoryCard
          key={category?.categoryId ?? `ideal-category-${categoryIndex}`}
          category={category}
          categoryIndex={categoryIndex}
          noDivider={categoryIndex === 0}
        />
      ))}
    </>
  )
}

export function IdealSipCategoryListStickyBar({ categoryCount }: { categoryCount: number }) {
  return (
    <div className="sticky top-[-2px] z-10 flex items-center justify-between bg-white px-5 py-4 border-b border-[#E8EBEA]">
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">
        {categoryCount} {categoryCount === 1 ? 'Category' : 'Categories'}
      </div>
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">Ideal range</div>
    </div>
  )
}
